import React, { useEffect, useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DiscountCodeInput } from './discount-code-input';
import { getTranslationValue, sanitizeTranslation } from '@/utils/translations';
import { InputValueMap } from '@/utils/new-order';
import { InteractionManager, Platform, View } from 'react-native';
import { ItemListOption } from '@/contexts/item-context-types';
import { OrderSummaryInput } from '@domain/configuration/types';
import { RecyclerListRef } from '@/components/recycler-list';
import { SummaryTextInput, SummaryTextInputRef } from './summary-text-input';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useKeyboardVisibility } from '@/hooks/use-keyboard-visibility';

export interface InputErrorMap {
  [id: string]: string;
}

interface Props {
  appliedDiscountCodes: string[];
  disabled?: boolean;
  draftOrderUUID: string;
  errorMap: InputErrorMap;
  inputs: OrderSummaryInput[];
  listRef: React.RefObject<RecyclerListRef<ItemListOption> | null>;
  onChange: (id: string, title: string, value: string) => void;
  onDiscountCodesApplied: () => Promise<void> | void;
  showDiscountCodeInput: boolean;
  valueMap: InputValueMap;
}

export const OrderSummaryInputs: React.FC<Props> = ({
  appliedDiscountCodes,
  disabled = false,
  draftOrderUUID,
  errorMap,
  inputs,
  listRef,
  onChange,
  onDiscountCodesApplied,
  showDiscountCodeInput,
  valueMap,
}) => {
  const isKeyboardVisible = useKeyboardVisibility();
  const [styles] = useStyles();
  const orderSummarySectionsEnabled = useDynamicConfigValue(
    'order_summary_sections_enabled',
  );

  const inputRefMapRef = useRef<{ [id: string]: SummaryTextInputRef }>({});
  const [focusedInputRef, setFocusedInputRef] =
    useState<SummaryTextInputRef | null>(null);

  useEffect(() => {
    // Focus on the first input that has an error
    for (const input of inputs) {
      if (errorMap[input.id]) {
        inputRefMapRef.current[input.id]?.focus();
        return;
      }
    }
  }, [errorMap, inputs]);

  useEffect(() => {
    const scrollViewNode = listRef.current?.getScrollableNode();
    if (focusedInputRef && isKeyboardVisible && scrollViewNode) {
      // Scroll to the first input within the list when the keyboard opens
      focusedInputRef.measureLayout(scrollViewNode, (_, top) => {
        InteractionManager.runAfterInteractions(() => {
          listRef.current?.scrollTo({ animated: true, y: top });
        });
      });
    }
  }, [focusedInputRef, isKeyboardVisible, listRef]);

  const handleInputFocus = (inputID: string) => {
    setFocusedInputRef(inputRefMapRef.current[inputID] ?? null);

    // Special handling for the last input - web only
    if (Platform.OS === 'web') {
      const isLastInput = inputs[inputs.length - 1]?.id === inputID;
      if (isLastInput && listRef.current) {
        // Force scroll to end for the last input to ensure it's visible
        setTimeout(() => {
          listRef.current?.scrollToEnd({ animated: true });
        }, 150);
      }
    }
  };

  return (
    <View
      style={[
        styles.base,
        orderSummarySectionsEnabled ? styles.baseSections : null,
      ]}
    >
      {showDiscountCodeInput ? (
        <DiscountCodeInput
          appliedCodes={appliedDiscountCodes}
          draftOrderUUID={draftOrderUUID}
          onCodesApplied={onDiscountCodesApplied}
        />
      ) : null}
      {inputs.map((input) => {
        const title = getTranslationValue(sanitizeTranslation(input.title));
        return (
          <SummaryTextInput
            disabled={disabled}
            error={errorMap[input.id] ?? ''}
            id={input.id}
            key={input.id}
            numberOfLines={input.type === 'textarea' ? 4 : undefined}
            onBlur={() => setFocusedInputRef(null)}
            onChange={(value) => onChange(input.id, title, value)}
            onFocus={() => handleInputFocus(input.id)}
            placeholder={
              input.placeholder
                ? getTranslationValue(sanitizeTranslation(input.placeholder))
                : undefined
            }
            ref={(ref) => {
              if (ref) {
                inputRefMapRef.current[input.id] = ref;
              } else {
                delete inputRefMapRef.current[input.id];
              }
            }}
            required={input.required}
            subtitle={
              input.subtitle
                ? getTranslationValue(sanitizeTranslation(input.subtitle))
                : undefined
            }
            title={title}
            trackingData={{
              name: `order_summary.${input.id}_button`,
              properties: { draftOrderUUID },
            }}
            value={valueMap[input.id]?.value ?? ''}
          />
        );
      })}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    borderColor: theme.colors.background,
    borderTopWidth: 2,
    paddingHorizontal: 16,
  },
  baseSections: {
    borderTopWidth: 0,
    paddingHorizontal: 0,
  },
}));
